import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';

interface AffiliateConfig {
  id: string;
  vendor_name: string;
  base_search_url: string;
  affiliate_parameter: string;
  affiliate_code: string;
  created_at: string;
}

interface AffiliateClick {
  id: string;
  user_id: string | null;
  conversation_id: string | null;
  category: string | null;
  vendor_name: string | null;
  search_query: string | null;
  final_url_clicked: string | null;
  clicked_at: string;
  users?: { email: string; name: string | null } | null;
}

interface AffiliateAnalytics {
  clicksByDay: Record<string, number>;
  clicksByVendor: { vendor: string; clicks: number }[];
  topVendors: Record<string, number>;
  totalClicks: number;
}

export const AffiliateVendorManagement: React.FC = () => {
  const [configs, setConfigs] = useState<AffiliateConfig[]>([]);
  const [clicks, setClicks] = useState<AffiliateClick[]>([]);
  const [analytics, setAnalytics] = useState<AffiliateAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<AffiliateConfig | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    vendor_name: '',
    base_search_url: '',
    affiliate_parameter: '',
    affiliate_code: ''
  });
  const [filterVendor, setFilterVendor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const result = await callAdmin('get_affiliate_configs');
      if (result.success) setConfigs((result.data as AffiliateConfig[]) || []);
      else setError(result.error || 'Failed to fetch affiliate configs');
    } catch (err) {
      setError('Failed to fetch affiliate configs');
      console.error('Error fetching affiliate configs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClicks = useCallback(async () => {
    try {
      const result = await callAdmin('get_affiliate_clicks', { data: { vendor_name: filterVendor || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, limit: 200 } });
      if (result.success) {
        const data = result.data as { clicks: AffiliateClick[], total: number, page: number, limit: number }
        setClicks(data.clicks || [])
      }
    } catch (err) {
      console.error('Error fetching affiliate clicks:', err);
    }
  }, [filterVendor, dateFrom, dateTo]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const result = await callAdmin('get_affiliate_analytics', { data: { date_from: dateFrom || undefined, date_to: dateTo || undefined } });
      if (result.success) {
        const data = result.data as { vendor: string; clicks: number }[]
        const vendorStats = data?.reduce((acc, item) => {
          acc[item.vendor] = item.clicks || 0
          return acc
        }, {} as Record<string, number>) || {}

        setAnalytics({
          clicksByDay: {},
          clicksByVendor: data || [],
          topVendors: vendorStats,
          totalClicks: data?.reduce((sum, item) => sum + (item.clicks || 0), 0) || 0
        })
      }
    } catch (err) {
      console.error('Error fetching affiliate analytics:', err);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchConfigs();
    fetchClicks();
    fetchAnalytics();
  }, [fetchConfigs, fetchClicks, fetchAnalytics]);

  const handleCreateConfig = () => {
    setEditForm({ vendor_name: '', base_search_url: '', affiliate_parameter: '', affiliate_code: '' });
    setShowCreateModal(true);
  };

  const handleEditConfig = (config: AffiliateConfig) => {
    setSelectedConfig(config);
    setEditForm({
      vendor_name: config.vendor_name,
      base_search_url: config.base_search_url,
      affiliate_parameter: config.affiliate_parameter,
      affiliate_code: config.affiliate_code
    });
    setShowEditModal(true);
  };

  const submitCreateConfig = async () => {
    try {
      const result = await callAdmin('create_affiliate_config', { data: editForm });
      if (result.success) { setShowCreateModal(false); fetchConfigs(); }
      else setError(result.error || 'Failed to create config');
    } catch (err) {
      setError('Failed to create config');
      console.error('Error creating config:', err);
    }
  };

  const submitUpdateConfig = async () => {
    if (!selectedConfig) return;
    try {
      const result = await callAdmin('update_affiliate_config', { resourceId: selectedConfig.id, data: editForm });
      if (result.success) { setShowEditModal(false); fetchConfigs(); }
      else setError(result.error || 'Failed to update config');
    } catch (err) {
      setError('Failed to update config');
      console.error('Error updating config:', err);
    }
  };

  const handleDeleteConfig = async (id: string) => {
    if (!confirm('Delete this affiliate config?')) return;
    try {
      const result = await callAdmin('delete_affiliate_config', { resourceId: id });
      if (result.success) fetchConfigs();
      else setError(result.error || 'Failed to delete config');
    } catch (err) {
      setError('Failed to delete config');
      console.error('Error deleting config:', err);
    }
  };

  const applyFilters = async () => {
    await Promise.all([fetchClicks(), fetchAnalytics()]);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Affiliate Programs</h2>
          <p className="text-gray-600">Manage vendor affiliate settings and view click analytics</p>
        </div>
        <Button onClick={handleCreateConfig} className="px-6">
          Add Vendor
        </Button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-10">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Affiliate Vendors</h3>
        <Card className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base URL</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Param</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {configs.map((cfg) => (
                  <tr key={cfg.id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{cfg.vendor_name}</td>
                    <td className="px-4 py-2 text-sm text-blue-600 truncate max-w-md"><a href={cfg.base_search_url} target="_blank" rel="noreferrer">{cfg.base_search_url}</a></td>
                    <td className="px-4 py-2 text-sm text-gray-900">{cfg.affiliate_parameter}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{cfg.affiliate_code}</td>
                    <td className="px-4 py-2 text-sm">
                      <button onClick={() => handleEditConfig(cfg)} className="text-blue-600 hover:text-blue-800 mr-3">Edit</button>
                      <button onClick={() => handleDeleteConfig(cfg.id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <Input value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)} placeholder="e.g., amazon" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button onClick={applyFilters} className="w-full">Apply</Button>
          </div>
        </div>
      </Card>

      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Clicks by Day</h3>
            <div className="space-y-2 text-sm text-gray-700">
              {Object.entries(analytics?.clicksByDay || {}).slice(0, 14).map(([day, count]) => (
                <div key={day} className="flex items-center justify-between">
                  <span>{day}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Vendors</h3>
            <div className="space-y-2 text-sm text-gray-700">
              {Object.entries(analytics?.topVendors || {}).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([vendor, count]) => (
                <div key={vendor} className="flex items-center justify-between">
                  <span className="capitalize">{vendor}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Clicks</h3>
        <Card className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Query</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(clicks || []).map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{new Date(c.clicked_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{c.vendor_name}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{c.search_query}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{c.users?.email || 'Anon'}</td>
                    <td className="px-4 py-2 text-sm text-blue-600 truncate max-w-md"><a href={c.final_url_clicked || '#'} target="_blank" rel="noreferrer">{c.final_url_clicked}</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {(showEditModal || showCreateModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showCreateModal ? 'Create Affiliate Vendor' : 'Edit Affiliate Vendor'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
                  <Input value={editForm.vendor_name} onChange={(e) => setEditForm({ ...editForm, vendor_name: e.target.value })} className="w-full" placeholder="Amazon" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate Param *</label>
                  <Input value={editForm.affiliate_parameter} onChange={(e) => setEditForm({ ...editForm, affiliate_parameter: e.target.value })} className="w-full" placeholder="tag" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Search URL *</label>
                  <Input value={editForm.base_search_url} onChange={(e) => setEditForm({ ...editForm, base_search_url: e.target.value })} className="w-full" placeholder="https://www.amazon.com/s" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate Code *</label>
                  <Input value={editForm.affiliate_code} onChange={(e) => setEditForm({ ...editForm, affiliate_code: e.target.value })} className="w-full" placeholder="myaffiliate-20" />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button onClick={() => { setShowEditModal(false); setShowCreateModal(false); }} className="px-4 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400">Cancel</Button>
                <Button onClick={showCreateModal ? submitCreateConfig : submitUpdateConfig} className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700">{showCreateModal ? 'Create Vendor' : 'Update Vendor'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
